<template>
  <div class="auto-mode-wrapper" :class="{ 'exiting': isExiting }">
    <!-- Border around entire viewport -->
    <div class="auto-mode-border"></div>

    <!-- Top left badge -->
    <div class="auto-mode-badge-left">
      Auto Mode
      <span v-if="currentQueue" class="queue-name"> {{ currentQueue }}</span>
    </div>

    <!-- Top right exit badge -->
    <div class="auto-mode-badge-right" @click="exitAutoMode">
      Exit Auto
      <i class="fa fa-close"></i>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';

@Component
export default class AutoModeIndicator extends Vue {
  private isExiting = false;

  get currentQueue(): string | null {
    return this.$store.currentAutoModeQueue;
  }

  mounted(): void {
    document.addEventListener('keyup', this.handleEscKey);
  }

  beforeDestroy(): void {
    document.removeEventListener('keyup', this.handleEscKey);
  }

  handleEscKey(e: KeyboardEvent): void {
    if (e.code === 'Escape' && e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
      this.exitAutoMode();
    }
  }

  exitAutoMode(): void {
    // Trigger exit animation
    this.isExiting = true;

    // Wait for animation to complete before clearing state
    setTimeout(() => {
      this.$store.autoModeEnabled = false;
      this.$store.currentAutoModeQueue = null;
      this.$store.lastAutoModeResponse = null;

      this.$notification({
        message: 'Auto Mode has been exited',
        timeout: 3000,
        icon: "now-ui-icons ui-1_bell-53",
        horizontalAlign: "center",
        verticalAlign: "top",
        type: "info"
      });
    }, 400); // Match animation duration
  }
}
</script>

<style scoped>
.auto-mode-wrapper {
  pointer-events: none;
}

.auto-mode-border {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 4px solid #ff6b35;
  z-index: 9998;
  pointer-events: none;
  animation: border-appear 0.5s ease-out;
}

@keyframes border-appear {
  0% {
    opacity: 0;
    border-width: 0px;
  }
  50% {
    opacity: 1;
    border-width: 8px;
  }
  100% {
    border-width: 4px;
  }
}

.auto-mode-badge-left {
  position: fixed;
  top: 0;
  left: 0;
  background: #ff6b35;
  color: white;
  padding: 4px 10px 2px 10px;
  line-height: 1;
  border-bottom-right-radius: 20px;
  z-index: 9999;
  font-size: 12px;
  font-weight: 600;
  pointer-events: none;
  border-right: 4px solid #ff6b35;
  border-bottom: 4px solid #ff6b35;
  animation: badge-slide-in-left 0.5s ease-out;

  .queue-name {
    font-weight: 400;
    margin-left: 4px;
  }
}

@keyframes badge-slide-in-left {
  0% {
    transform: translate(-100%, -100%);
    opacity: 0;
  }
  60% {
    transform: translate(5px, 5px);
  }
  100% {
    transform: translate(0, 0);
    opacity: 1;
  }
}

.auto-mode-badge-right {
  position: fixed;
  top: 0;
  right: 0;
  background: #ff6b35;
  color: white;
  padding: 4px 10px 2px 10px;
  border-bottom-left-radius: 20px;
  z-index: 9999;
  font-size: 12px;
  font-weight: 600;
  pointer-events: auto;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 4px solid #ff6b35;
  border-bottom: 4px solid #ff6b35;
  animation: badge-slide-in-right 0.5s ease-out;
}

@keyframes badge-slide-in-right {
  0% {
    transform: translate(100%, -100%);
    opacity: 0;
  }
  60% {
    transform: translate(-5px, 5px);
  }
  100% {
    transform: translate(0, 0);
    opacity: 1;
  }
}

/* Exit animations */
.exiting .auto-mode-border {
  animation: border-disappear 0.4s ease-in forwards;
}

.exiting .auto-mode-badge-left {
  animation: badge-slide-out-left 0.4s ease-in forwards;
}

.exiting .auto-mode-badge-right {
  animation: badge-slide-out-right 0.4s ease-in forwards;
}

@keyframes border-disappear {
  0% {
    border-width: 4px;
    opacity: 1;
  }
  50% {
    border-width: 8px;
  }
  100% {
    border-width: 0px;
    opacity: 0;
  }
}

@keyframes badge-slide-out-left {
  0% {
    transform: translate(0, 0);
    opacity: 1;
  }
  100% {
    transform: translate(-100%, -100%);
    opacity: 0;
  }
}

@keyframes badge-slide-out-right {
  0% {
    transform: translate(0, 0);
    opacity: 1;
  }
  100% {
    transform: translate(100%, -100%);
    opacity: 0;
  }
}
</style>
